import React from 'react';

import { AnalyticsProvider } from '@backrs_ui/common-ui';

import { isAnalyticsEnabled, isSessionRecordingDisabled } from '../analytics/env';

interface RootAnalyticsProviderProps {
  children: React.ReactNode;
}

export const RootAnalyticsProvider: React.FC<RootAnalyticsProviderProps> = ({ children }) => {
  const apiKey = process.env.REACT_APP_POST_HOG_PROJECT_API_KEY;
  const defaultApiHost = process.env.REACT_APP_POSTHOG_API_HOST;
  const analyticsEnabled = isAnalyticsEnabled();
  const defaultUiHost = process.env.REACT_APP_POSTHOG_UI_HOST;
  const sessionRecordingDisabled = isSessionRecordingDisabled();

  return (
    <AnalyticsProvider
      apiKey={apiKey}
      apiHost={defaultApiHost}
      enabled={analyticsEnabled}
      uiHost={defaultUiHost}
      options={{
        person_profiles: 'always',
        disable_session_recording: sessionRecordingDisabled,
        mask_all_element_attributes: false,
        mask_all_text: true,
        // Auto-pageview is unreliable here: PostHog's pushState hook doesn't
        // catch React Router 6 navigations on this codebase, so SPA navs
        // silently miss most $pageview events. AnalyticsPageView (mounted
        // in App.tsx via PostHogPageViewShell) fires pageviews manually on
        // location.pathname change and synthesizes $pageview_id /
        // $prev_pageview_* so replay→event linkage and Paths-insight
        // breadcrumbs keep working.
        capture_pageview: false,
        session_recording: {
          maskAllInputs: true,
        },
        // Race: events captured between SDK init and the `loaded` callback
        // (which runs after /decide resolves) would miss the `app`
        // super-property if we only used `loaded.register`. `before_send`
        // tags every outbound event including the bootstrap ones;
        // `loaded.register` persists `app` to localStorage so future
        // sessions/tabs inherit it. Both required — removing either
        // regresses.
        before_send: (event) => {
          if (!event) return event;
          // Don't touch the session-recording stream. PostHog routes
          // $snapshot events through before_send, and reshaping the event
          // via spread breaks the snapshot uploader — recordings stop
          // landing. Snapshots are internal to recording, no `app` tag
          // needed.
          if (event.event === '$snapshot') return event;

          return { ...event, properties: { ...event.properties, app: 'grow' } };
        },
        loaded: (ph) => ph.register({ app: 'grow' }),
      }}
    >
      {children}
    </AnalyticsProvider>
  );
};
